import React, { useEffect } from 'react';
import Editor from "@monaco-editor/react";
import { Grid, Typography, Tooltip, Icon } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { editView } from "../../../store/views/actions";
import { getViews } from "../../../store/views/selectors";
import Button from '@material-ui/core/Button';
import { handleSnackBar, invokeGetService, invokePostService, setReRender, setCodeEditStatus, handleTopRightInfo } from "../../../store/global/actions";
import { getIsDocumentClicked, getCodeEditStatus, getReRender } from '../../../store/global/selectors';
import { getDetailsFromUrl } from '../../../services/blueprint'
import CodeSaveAlert from '../alerts/CodeSaveAlert';
import { useHistory } from 'react-router-dom';
import Checkbox from '@material-ui/core/Checkbox';
import RefreshIcon from '@material-ui/icons/Refresh';
import { generateViewsCode } from '../../../services/entities/DashboardBuilder.js';
import { setView } from "../../../store/views/actions.js";
import { modifyViewsSchemaWithMappedFields, generateViewsSampleData } from '../../../utils/DashboardBuilder.js';

const useStyles = makeStyles((theme) => ({
    root: {
        textAlign: 'center'
    },
    iconTooltip: {
        cursor: "pointer",
    },
    iconContainer: {
        marginRight: 18
    },
    icon: {
        fontSize: 25,
    },
}));

export default function DataBuilder() {

    const classes = useStyles();
    const dispatch = useDispatch();
    const editorRef = useRef(null);
    const isAutoFormatting = useRef(false);
    const history = useHistory();

    const selectedView = useSelector(getViews);
    const codeEditStatus = useSelector(getCodeEditStatus);
    const clickStatus = useSelector(getIsDocumentClicked);
    const reRender = useSelector(getReRender);

    

    const [code, setCode] = useState(null)
    const [loading, setLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isShipCodeToController, setIsShipCodeToController] = useState(false);
    const [openSaveAlert, setOpenSaveAlert] = useState(false);

    const { slug, blueprintId, entityId } = getDetailsFromUrl();
    const handleSchemaIcons = (schema) => {
        schema.forEach(section => {
            section?.children?.forEach(widget => {
                if (widget.type === "counter" && widget.customicon) {
                    const assetUrl = window.location.origin;

                    if (widget?.iconSource === 'custom' || widget?.isCustomIcon) {
                        const assetDirectoryName = `${blueprintId}_${slug}`;
                        widget.iconsrc = `${assetUrl}/blueprintAssets/${assetDirectoryName}/${widget.customicon}`;
                    } else if (widget?.iconSource === 'library') {
                        widget.iconsrc = `${assetUrl}/cached_icons/${widget.customicon}`;
                    } else {
                        widget.iconsrc = `${assetUrl}/node_icons/${widget.customicon}.svg`;
                    }
                }
            })
        })

        return schema;
    }

    const loadDataBuilder = async () => {
        try {
            setLoading(true);
            const response = await dispatch(invokeGetService('getDashboardBuilder', [entityId]))
            if(response === undefined || response === null){
                throw new Error(`Fetch data failed`)
            }
            const { status, data, message } = response;
            if (status === false) {
                setLoading(false);
                dispatch(handleSnackBar({ open: true, message: message, severity: 'error' }))
                return;
            }
            const { view, styles, clusterlets, transactions, events, messages, sections, fields, assets } = data;
            let modifiedSchema = modifyViewsSchemaWithMappedFields({ schema: view?.view_schema, clusterlets, transactions, events, messages, sections, fields });
            modifiedSchema = handleSchemaIcons(modifiedSchema);
            view.view_schema = modifiedSchema;
            dispatch(setView(view));

            if (view.code !== "" && view.code) {
                updateEditorCode(view.code, true);
            } else {
                const sampleCode = generateViewsCode({})
                updateEditorCode(sampleCode, true);
            }

            const codeToController = view.configuration?.shipCodeToController ? view.configuration.shipCodeToController : false;
            setIsShipCodeToController(codeToController);

            setLoading(false);
        } catch (error) {
            console.error(`Loading databuilder failed. ${error}`);
            return ;
        }
    }

    useEffect(() => {
        loadDataBuilder();
    }, [entityId, reRender])


    useEffect(() => {
        // For triggering the save alert when closing the editor
        if (codeEditStatus) {
            if (code === null) {
                setOpenSaveAlert(true);
            } else {
                if (code !== editorRef?.current?.getValue()) {
                    setOpenSaveAlert(true);
                }
            }
        }

    }, [clickStatus]);


    const updateEditorCode = (code, isInitialFetch) => {
        if (editorRef?.current !== null) {
            if (isInitialFetch) {
                isAutoFormatting.current = true;
            }

            editorRef?.current?.setValue(code);
            formatCode();
            if (isInitialFetch) {
                setTimeout(() => {
                    const formatted = editorRef.current?.getValue();
                    if (formatted) setCode(formatted);
                    isAutoFormatting.current = false;
                }, 1000);
            }
        }
    }
    

    const formatCode = () => {
        if (editorRef.current) {
            editorRef.current.getAction("editor.action.formatDocument").run();
        }
    }


    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;

        // setTimeout(() => {   // Ensure initial formatting when the editor is ready
        //     formatCode();
        // }, 100);
    }

    const handleEditorChange = (value, event) => {
        if (isAutoFormatting.current) return;
        if (codeEditStatus === false && !loading) {
            dispatch(setCodeEditStatus(true));
        } else {
            if (code !== null && code === value) {
                dispatch(setCodeEditStatus(false));
            }
        }
    }

    const onClose = () => {
        setOpenSaveAlert(false);
        dispatch(setCodeEditStatus(false));
    }

    const onSave = () => {
        const jsCode = editorRef.current.getValue();
        const updatedView = {
            ...selectedView,
            code: jsCode,
            configuration: { shipCodeToController: isShipCodeToController }
        }
        setCode(jsCode)
        dispatch(invokePostService('updateView', { view: updatedView }, [selectedView._id])).then(response => {
            setIsUpdating(false)
            setOpenSaveAlert(false);
            dispatch(setCodeEditStatus(false));
            if (response.status == true) {
                dispatch(editView(updatedView));
                dispatch(handleSnackBar({ open: true, message: "Code updated", severity: "success", }));
            } else {
                if (response.code === 255) {
                    history.push(`/blueprint/${slug}/${blueprintId}/system-model`);
                    dispatch(setReRender());
                }
            }
        });
    }


    const setDemoData = async () => {

        setLoading(true);
        const { status, data } = await dispatch(invokeGetService('getDashboardBuilder', [selectedView._id]))
        setLoading(false);
        if (status == false) {
            return;
        }
        const { view, clusterlets, transactions, events, messages, sections, fields, assets } = data;
        dispatch(setView(view));

        const schema = JSON.parse(JSON.stringify(view.view_schema));
        const modifiedSchema = modifyViewsSchemaWithMappedFields({ schema, clusterlets, transactions, events, messages, sections, fields });
        const schemaData = generateViewsSampleData({ viewsSchema: modifiedSchema, assets, blueprintId, slug, clusterlets});
        const demoData = generateViewsCode(schemaData);
        updateEditorCode(demoData, false);

    }

    const handleCheckBoxChange = (event) => {
        const updatedView = {
            ...selectedView,
            configuration: { shipCodeToController: event.target.checked }
        }
        setIsShipCodeToController(event.target.checked);
        dispatch(handleTopRightInfo({ message: `Data builder ${event.target.checked ? "enabling" : "disabling"} for Controller...`, open: true, }));
        dispatch(invokePostService('updateView', { view: updatedView }, [selectedView._id])).then(response => {
            if (response.status == true) {
                dispatch(handleTopRightInfo({}));
                dispatch(handleSnackBar({ open: true, message: `Data builder ${event.target.checked ? "enabled" : "disabled"} for Controller`, severity: "success", }));
                dispatch(editView(updatedView));
            } else {
                dispatch(handleTopRightInfo({}));
                if (response.code === 255) {
                    const { slug, blueprintId } = getDetailsFromUrl();
                    history.push(`/blueprint/${slug}/${blueprintId}/system-model`);
                    dispatch(setReRender());
                }
            }
        })

    }


    return (
        <div className={classes.root}>
            <Grid container id={"simulate-tab-content"} direction={"row"} spacing={3}>
                <Grid id='dataBuilderToController' item xs={6} style={{ display: 'flex', alignItems: 'center' }} >
                    <Checkbox id='dataBuilderCheckbox' color='primary' checked={isShipCodeToController} onChange={handleCheckBoxChange} />
                    <Typography> Enable for Controller </Typography>
                </Grid>
                <Grid  id={"set-demodata-container"} item xs={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Tooltip title="Set demo data" className={classes.iconTooltip}>
                        <Icon id="refresh" onClick={setDemoData} className={classes.iconContainer} disabled={loading}>
                            <RefreshIcon className={classes.icon} color="primary" />
                        </Icon>
                    </Tooltip>
                </Grid>
                <Grid id={"editor-container"} item xs={12}>
                    <Editor
                        height="68vh"
                        defaultLanguage="javascript"
                        language="javascript"
                        theme="vs-dark"
                        path={entityId}
                        defaultValue={code}
                        onMount={handleEditorDidMount}
                        loading={"Loading..."}
                        onChange={handleEditorChange}
                        options={{
                            formatOnType: true,
                            formatOnPaste: true,
                        }}
                    />
                </Grid>

                <Grid id={"update-button-container"} item xs={12} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }} >
                    <Button id={"update-button"} onClick={() => { setIsUpdating(true); onSave(); }} disabled={isUpdating} color="primary" variant="contained" style={{ marginLeft: 10 }} >
                        {isUpdating ? 'Updating..' : 'Update'}
                    </Button>
                </Grid>

            </Grid>

            {openSaveAlert && <CodeSaveAlert
                onClose={onClose}
                onSave={onSave}
            />}

        </div>

    )
}